import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Alert,
  Button,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Person as PersonIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { apiClient } from '../../services/apiClient';
import { LoadingScreen } from '../../components/common/LoadingScreen';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import UserLocationsTab from '../../components/admin/UserLocationsTab';
import UserLocationGroupsTab from '../../components/admin/UserLocationGroupsTab';
import UserFeatureFlagsTab from '../../components/admin/UserFeatureFlagsTab';
import UserNotificationPreferenceTab from '../../components/admin/UserNotificationPreferenceTab';
import UserDetailsTab from '../../components/admin/UserDetailsTab';
import { useUserRoles } from '../../hooks/useUserRoles';

interface UserDetail {
  uuid: string;
  name: string;
  email: string;
  type: string;
  api_access_enabled: boolean;
  created_at: string;
  roles: string[];
}

export default function UserDetailPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const { isSuperAdmin, isAdmin, userUuid } = useUserRoles();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('details');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    loadUser();
  }, [uuid]);

  const loadUser = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<UserDetail>(`/admin/users/${uuid}`);
      setUser(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const isSelf = user && userUuid === user.uuid;
  const canDeleteUser = user && isAdmin() && !user.roles.includes('SUPER_ADMIN') && !isSelf;

  const handleDeleteUser = async () => {
    if (!user) return;
    try {
      await apiClient.delete(`/admin/users/${user.uuid}`);
      navigate('/admin/users');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading user..." />;
  }

  if (error || !user) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">
            {error || 'User not found'}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/users')}
            sx={{ mt: 2 }}
          >
            Back to Users
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/users')}
            sx={{ mb: 2 }}
          >
            Back to Users
          </Button>
          <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PersonIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: 'primary.main' }} />
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                  {user.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                  {user.roles.map((role) => (
                    <Chip
                      key={role}
                      label={role}
                      color={role === 'SUPER_ADMIN' ? 'error' : role === 'ORG_ADMIN' ? 'warning' : 'primary'}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            </Box>
            {canDeleteUser && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                size="small"
              >
                Delete User
              </Button>
            )}
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Details" value="details" />
            <Tab label="Locations" value="locations" />
            {isSuperAdmin() && <Tab label="Location Groups" value="location-groups" />}
            {isSuperAdmin() && <Tab label="Feature Flags" value="feature-flags" />}
            <Tab label="Notifications" value="notifications" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 'details' && <UserDetailsTab user={user} onUserUpdated={loadUser} />}
        {activeTab === 'locations' && <UserLocationsTab userUuid={user.uuid} />}
        {isSuperAdmin() && activeTab === 'location-groups' && <UserLocationGroupsTab userUuid={user.uuid} />}
        {isSuperAdmin() && activeTab === 'feature-flags' && <UserFeatureFlagsTab userUuid={user.uuid} />}
        {activeTab === 'notifications' && <UserNotificationPreferenceTab userUuid={user.uuid} />}

        <ConfirmDialog
          open={deleteDialogOpen}
          title="Delete User"
          message={`Are you sure you want to delete "${user.name}" (${user.email})? This action cannot be undone. The user will be removed from the system and their account will be deleted.`}
          confirmText="Delete User"
          confirmColor="error"
          onConfirm={handleDeleteUser}
          onCancel={() => setDeleteDialogOpen(false)}
        />
      </Box>
    </Container>
  );
}
